import type { RaceEventTrigger, RaceEventType } from './types';
import type { CircuitSpec, LapGateRule } from './laps';

// ---------------------------------------------------------------------------
// 자유형(field) 맵 프레임워크 — 신규 6종 맵의 지오메트리·장애물·존 스펙.
// 물리와 렌더러가 같은 스펙과 같은 순수 함수를 공유해 리플레이가 어긋나지 않게 한다.
// ---------------------------------------------------------------------------

export interface FieldPoint {
  readonly x: number;
  readonly y: number;
}

export interface FieldRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export type FieldShape =
  | { readonly kind: 'room'; readonly x: number; readonly y: number; readonly w: number; readonly h: number; readonly r?: number }
  | { readonly kind: 'disc'; readonly x: number; readonly y: number; readonly r: number }
  | { readonly kind: 'ellipse'; readonly x: number; readonly y: number; readonly rx: number; readonly ry: number }
  | { readonly kind: 'pipe'; readonly points: readonly FieldPoint[]; readonly width: number; readonly endWidth?: number }
  | { readonly kind: 'poly'; readonly points: readonly FieldPoint[] };

export interface FieldGateSchedule {
  readonly type: 'cycle' | 'open-at' | 'crowd' | 'event-close';
  /** cycle: 주기(초) */
  readonly period?: number;
  /** gate-frenzy 이벤트 정점의 주기(초). 미지정 시 기존 0.38배. */
  readonly eventPeriod?: number;
  /** cycle: 주기 중 열려 있는 시간(초) */
  readonly openSeconds?: number;
  /** cycle: 위상(초) · lift: 위상 */
  readonly phase?: number;
  /** open-at: 이 시각(초) 이후 영구 개방. Infinity면 이벤트로만 개방 */
  readonly at?: number;
  /** crowd: 집결 트리거 마블 수 */
  readonly count?: number;
  /** crowd: 첫 도착 후 최대 대기(초) */
  readonly waitSeconds?: number;
  /** crowd: 개방 유지(초) */
  readonly holdSeconds?: number;
  /** crowd: 집결 감지 반경 */
  readonly radius?: number;
}

export type FieldObstacle =
  | { readonly kind: 'paddle'; readonly id: string; readonly x: number; readonly y: number; readonly length: number; readonly thickness: number; readonly speed: number; readonly phase: number; readonly reverseEvery?: number; readonly eventType?: RaceEventType }
  | { readonly kind: 'disk'; readonly id: string; readonly x: number; readonly y: number; readonly r: number; readonly hubR: number; readonly speed: number; readonly grip: number; readonly eventType?: RaceEventType }
  | { readonly kind: 'bumper'; readonly id: string; readonly x: number; readonly y: number; readonly r: number; /** 기본 0.95 — 닫힌 광장 안 범퍼는 낮춰 무한 핀볼을 막는다 */ readonly restitution?: number }
  | { readonly kind: 'pin'; readonly id: string; readonly x: number; readonly y: number; readonly r: number; readonly sink?: boolean }
  | { readonly kind: 'bar'; readonly id: string; readonly x: number; readonly y: number; readonly length: number; readonly thickness: number; readonly angle: number }
  | { readonly kind: 'seesaw'; readonly id: string; readonly x: number; readonly y: number; readonly length: number; readonly thickness: number; readonly maxAngle: number; readonly spring: number; readonly damping: number; readonly eventType?: RaceEventType }
  | { readonly kind: 'flipper'; readonly id: string; readonly x: number; readonly y: number; readonly length: number; readonly thickness: number; readonly restAngle: number; readonly hitAngle: number; readonly period: number; readonly swingSeconds: number; readonly phase: number; readonly eventType?: RaceEventType }
  | { readonly kind: 'kicker'; readonly id: string; readonly x: number; readonly y: number; readonly length: number; readonly thickness: number; readonly restAngle: number; readonly hitAngle: number; readonly hitSeconds: number; /** 타격 후 같은 방향으로 한 바퀴 장전하는 시간 */ readonly returnSeconds: number; readonly cooldown: number; readonly triggerRadius: number; readonly triggerCount?: number; readonly phase?: number; readonly linkedLiftId?: string; readonly eventType?: RaceEventType }
  | { readonly kind: 'gate'; readonly id: string; readonly x: number; readonly y: number; readonly w: number; readonly h: number; /** 중심 기준 회전(라디안) — 나선 컷 등 비직교 게이트 */ readonly angle?: number; readonly schedule: FieldGateSchedule; readonly eventType?: RaceEventType; readonly lapRule?: LapGateRule }
  | { readonly kind: 'shutter'; readonly id: string; readonly x: number; readonly y: number; readonly r: number; readonly openSeconds: number; readonly closeSeconds: number; readonly phase: number; readonly eventType?: RaceEventType }
  | { readonly kind: 'rotor'; readonly id: string; readonly x: number; readonly y: number; readonly wingLength: number; readonly thickness: number; readonly speed: number; readonly hubR: number; /** 패들과 같은 감속/가속 전환. 생략하면 기존 단방향 회전. */ readonly reverseEvery?: number; readonly eventType?: RaceEventType }
  | { readonly kind: 'lift'; readonly id: string; readonly x: number; readonly boardSurfaceY: number; readonly crestSurfaceY: number; readonly width: number; readonly thickness: number; readonly dumpAngle: number; readonly period: number; readonly boardFraction: number; readonly riseFraction: number; readonly dumpFraction: number; readonly phase: number; readonly mouths: readonly FieldRect[]; readonly hatch?: FieldRect; readonly eventType?: RaceEventType };

export type FieldZone =
  | { readonly kind: 'accel'; readonly rect: FieldRect; readonly ax: number; readonly ay: number; readonly eventType?: RaceEventType; /** 이벤트 시 반전(기본) 대신 배율 증폭 */ readonly eventScale?: number }
  | {
    readonly kind: 'belt';
    readonly rect: FieldRect;
    /** 대각 벨트 — 있으면 rect 대신 캡슐(스파인 ± 폭/2)로 포함 판정한다. */
    readonly capsule?: { readonly points: readonly FieldPoint[]; readonly width: number };
    readonly vx: number;
    readonly vy?: number;
    readonly grip: number;
    /** 탑뷰 레인에서 벨트 직각 방향의 잔류 속도를 감쇠한다. 중력 맵에는 적용하지 않는다. */
    readonly lateralGrip?: number;
    /** 주기적으로 켜지는 역주행 패치. 꺼졌을 때는 아래의 정방향 벨트가 작동한다. */
    readonly cycle?: { readonly period: number; readonly onSeconds: number; readonly phase: number };
    readonly reversible?: boolean;
    readonly lane: number;
    /** 겹칠 때 높은 쪽 하나만 작동 (역주행 패치 > 크로스 벨트 > 레인) */
    readonly priority?: number;
    /** 이벤트 진행 중에만 작동하는 벨트 (역주행 패치 확장분) */
    readonly eventOnly?: boolean;
    /** 이벤트 진행 중 rect 대신 쓰는 확장 영역 */
    readonly eventRect?: FieldRect;
    readonly eventType?: RaceEventType;
    /** 렌더 강조색 (역주행 패치) */
    readonly tint?: string;
    /** false면 셰브런을 그리지 않는다 — 경사를 벨트 물리로 구현하는 맵용 */
    readonly chevrons?: boolean;
  }
  | { readonly kind: 'pump'; readonly rect: FieldRect; readonly ax: number; readonly ay: number; readonly period: number; readonly phase?: number }
  | { readonly kind: 'gravity'; readonly rect: FieldRect; readonly gx: number; readonly gy: number; readonly eventType?: RaceEventType; readonly eventGx?: number; readonly eventGy?: number; readonly label?: string }
  | { readonly kind: 'radial'; readonly x: number; readonly y: number; readonly rMin: number; readonly rMax: number; readonly inward: number; readonly spin: number; readonly halfPlane?: 'upper' | 'lower'; readonly eventType?: RaceEventType; readonly eventSpinScale?: number }
  | { readonly kind: 'guide'; readonly rect: FieldRect; readonly accel: number }
  | { readonly kind: 'wait'; readonly rect?: FieldRect; readonly circle?: { readonly x: number; readonly y: number; readonly r: number }; readonly calm?: number; readonly eventType?: RaceEventType; readonly eventCalm?: number; /** false: 대기 없이 감속만 적용 */ readonly waitForRelease?: boolean };

export interface FieldGravityStation extends FieldPoint {
  readonly r: number;
  readonly progress: number;
  readonly gx: number;
  readonly gy: number;
}

export interface FieldGravityFlow {
  readonly stations: readonly FieldGravityStation[];
  readonly blendSeconds: number;
  readonly eventType: RaceEventType;
  readonly eventBlendSeconds: number;
}

export interface FieldEventSpec {
  readonly type: RaceEventType;
  readonly duration: number;
  readonly window: readonly [number, number];
  /** 랩 앵커 및 누적 진행도 창. live 스케줄러가 실제 물리 상태로 판정한다. */
  readonly trigger?: RaceEventTrigger;
  /** 핵심 기믹 이벤트. 나머지 슬롯만 시드로 추첨한다. */
  readonly required?: boolean;
}

export interface FieldEventZone {
  /** 순환 맵의 다음 랩 전용 장치가 첫 랩 공에 의해 발동하지 않도록 한다. */
  readonly minLap?: number;
  readonly type: RaceEventType;
  readonly rect?: FieldRect;
  readonly circle?: { readonly x: number; readonly y: number; readonly r: number };
}

export interface FieldLandmark {
  readonly x: number;
  readonly y: number;
  readonly label: string;
}

export type FieldSpawn =
  | { readonly kind: 'rect'; readonly x: number; readonly y: number; readonly w: number; readonly h: number; readonly pitch: number; readonly fill?: 'progress' | 'center' | 'spread' }
  | { readonly kind: 'arc'; readonly cx: number; readonly cy: number; readonly r: number; readonly from: number; readonly to: number; readonly rows: number; readonly pitch: number; readonly drift?: number };

export type FieldFinish =
  | { readonly kind: 'rect'; readonly x: number; readonly y: number; readonly w: number; readonly h: number }
  | { readonly kind: 'circle'; readonly x: number; readonly y: number; readonly r: number };

export interface FieldSpec {
  readonly shapes: readonly FieldShape[];
  readonly holes: readonly FieldShape[];
  /** 기본 중력 (px/s²) — gravity 존이 지역별로 덮어쓴다. */
  readonly gravity: { readonly x: number; readonly y: number };
  /** 마블 기본 선형 감쇠 (탑뷰 맵은 굴림 마찰을 크게) */
  readonly damping: number;
  /** Forward belts apply traction without braking away collision momentum. Reverse patches remain brakes. */
  readonly rollingBelts?: boolean;
  /** Brief elastic side-wall release after sustained gliding; never floors, gates or narrow mouths. */
  readonly wallBounce?: boolean;
  readonly spawn: FieldSpawn;
  readonly startGate?: FieldRect & { readonly angle?: number };
  /** 두 방향으로 연결된 출발장도 셔플 중에는 완전히 닫는다. */
  readonly extraStartGates?: readonly (FieldRect & { readonly angle?: number })[];
  readonly circuit?: CircuitSpec;
  readonly finish: FieldFinish;
  readonly obstacles: readonly FieldObstacle[];
  readonly zones: readonly FieldZone[];
  readonly events: readonly FieldEventSpec[];
  readonly eventZones: readonly FieldEventZone[];
  readonly landmarks: readonly FieldLandmark[];
  /** 진행이 없을 때 넛지까지 기다리는 시간 (대기형 맵은 길게) */
  readonly stallSeconds: number;
  /** 이 시각(초) 이후 완주 보증 모드 — 게이트 개방 편향·리프트 가속·유도력 증가 */
  readonly rushAt: number;
  /** v3 맵 속도 캡 (px/s) — 미지정 시 엔진 전역 상한(≈920)만 적용 */
  readonly maxSpeed?: number;
  /** 맵 한정 중력 변조. 지역 중력을 적용한 뒤 배율과 속도 캡을 함께 보간한다. */
  readonly gravityEvent?: { readonly type: RaceEventType; readonly scale: number; readonly maxSpeed: number };
  /** 사발 안에서는 고정, 연결 회랑에서는 진행도에 따라 회전하는 마블별 중력. */
  readonly gravityFlow?: FieldGravityFlow;
  /** 병렬 레인 맵 — 가이드에서 크게 이탈 시 전역 재탐색으로 복귀 */
  readonly guideRecovery?: boolean;
  /** 복귀 트리거 거리(px, 기본 220) — 나선처럼 인접 구간이 가까우면 낮춘다 */
  readonly guideRecoveryDistance?: number;
  /** 좁은 곡관이 많은 맵 — 러시 부스트를 마블별 위상 펄스로 만들어 압력 아치를 계속 무너뜨린다 */
  readonly crowdAgitation?: boolean;
  /** v3 정체 워치독 — 저속이 seconds 이상 지속되면 가이드 방향 임펄스(px/s) */
  readonly velocityWatchdog?: { readonly minSpeed: number; readonly seconds: number; readonly impulse: number };
  /** v3 이벤트 규칙 — 이벤트 간 최소 간격(기본 6초) */
  readonly eventMinGapSeconds?: number;
  /** v3 이벤트 규칙 — 첫 결승 도착 몇 초 전부터 발동 금지(기본 3초) */
  readonly eventFinalGuardSeconds?: number;
  readonly eventScheduling?: 'live' | 'occupancy';
}

// ---------------------------------------------------------------------------
// SDF — 걷는 면(union) 안쪽이 음수. holes는 안쪽 벽(양수 섬).
// ---------------------------------------------------------------------------

/** 표시와 물리가 같은 시계·활성 조건을 사용한다. 이벤트 확장은 주기를 건너뛴다. */
export function beltIsActive(zone: Extract<FieldZone, { kind: 'belt' }>, time: number, eventActive: boolean): boolean {
  if (zone.eventOnly && !eventActive) return false;
  if (!zone.cycle || eventActive) return true;
  const { period, onSeconds, phase } = zone.cycle;
  return ((time + phase) % period + period) % period < onSeconds;
}

function sdfRoom(px: number, py: number, shape: Extract<FieldShape, { kind: 'room' }>): number {
  const radius = Math.min(shape.r ?? 18, shape.w / 2, shape.h / 2);
  const cx = shape.x + shape.w / 2;
  const cy = shape.y + shape.h / 2;
  const qx = Math.abs(px - cx) - (shape.w / 2 - radius);
  const qy = Math.abs(py - cy) - (shape.h / 2 - radius);
  const outside = Math.hypot(Math.max(qx, 0), Math.max(qy, 0));
  return outside + Math.min(Math.max(qx, qy), 0) - radius;
}

function sdfDisc(px: number, py: number, shape: Extract<FieldShape, { kind: 'disc' }>): number {
  return Math.hypot(px - shape.x, py - shape.y) - shape.r;
}

/** 1차 근사 타원 SDF — 0 등고선(윤곽)은 정확하고 축 위에서는 실거리와 일치한다. */
function sdfEllipse(px: number, py: number, shape: Extract<FieldShape, { kind: 'ellipse' }>): number {
  const dx = px - shape.x;
  const dy = py - shape.y;
  const kx = dx / shape.rx;
  const ky = dy / shape.ry;
  const f = Math.hypot(kx, ky);
  if (f < 1e-4) return -Math.min(shape.rx, shape.ry);
  const gradient = Math.hypot(kx / shape.rx, ky / shape.ry);
  return f * (f - 1) / Math.max(1e-6, gradient);
}

interface PipeRuntime {
  readonly points: readonly FieldPoint[];
  readonly cumulative: readonly number[];
  readonly total: number;
  readonly width: number;
  readonly endWidth: number;
}

function pipeRuntime(shape: Extract<FieldShape, { kind: 'pipe' }>): PipeRuntime {
  const cumulative: number[] = [0];
  for (let index = 1; index < shape.points.length; index += 1) {
    const previous = shape.points[index - 1];
    const point = shape.points[index];
    cumulative.push(cumulative[index - 1] + Math.hypot(point.x - previous.x, point.y - previous.y));
  }
  return {
    points: shape.points,
    cumulative,
    total: Math.max(1, cumulative[cumulative.length - 1]),
    width: shape.width,
    endWidth: shape.endWidth ?? shape.width,
  };
}

function sdfPipe(px: number, py: number, pipe: PipeRuntime): number {
  let best = Number.POSITIVE_INFINITY;
  for (let index = 0; index < pipe.points.length - 1; index += 1) {
    const a = pipe.points[index];
    const b = pipe.points[index + 1];
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const lengthSquared = Math.max(1e-6, abx * abx + aby * aby);
    const t = Math.max(0, Math.min(1, ((px - a.x) * abx + (py - a.y) * aby) / lengthSquared));
    const distance = Math.hypot(px - (a.x + abx * t), py - (a.y + aby * t));
    const arc = (pipe.cumulative[index] + Math.sqrt(lengthSquared) * t) / pipe.total;
    const halfWidth = (pipe.width + (pipe.endWidth - pipe.width) * arc) / 2;
    best = Math.min(best, distance - halfWidth);
  }
  return best;
}

function sdfPoly(px: number, py: number, shape: Extract<FieldShape, { kind: 'poly' }>): number {
  const points = shape.points;
  let distance = Number.POSITIVE_INFINITY;
  let sign = 1;
  for (let index = 0, previous = points.length - 1; index < points.length; previous = index, index += 1) {
    const a = points[previous];
    const b = points[index];
    const ex = b.x - a.x;
    const ey = b.y - a.y;
    const wx = px - a.x;
    const wy = py - a.y;
    const t = Math.max(0, Math.min(1, (wx * ex + wy * ey) / Math.max(1e-6, ex * ex + ey * ey)));
    distance = Math.min(distance, Math.hypot(wx - ex * t, wy - ey * t));
    const conditions = [py >= a.y, py < b.y, ex * wy > ey * wx];
    if (conditions.every(Boolean) || conditions.every((value) => !value)) sign = -sign;
  }
  return sign * distance;
}

interface CompiledField {
  readonly shapes: readonly FieldShape[];
  readonly holes: readonly FieldShape[];
  readonly pipes: ReadonlyMap<FieldShape, PipeRuntime>;
}

const compiledCache = new WeakMap<FieldSpec, CompiledField>();

function compileField(spec: FieldSpec): CompiledField {
  const cached = compiledCache.get(spec);
  if (cached) return cached;
  const pipes = new Map<FieldShape, PipeRuntime>();
  for (const shape of [...spec.shapes, ...spec.holes]) {
    if (shape.kind === 'pipe') pipes.set(shape, pipeRuntime(shape));
  }
  const compiled: CompiledField = { shapes: spec.shapes, holes: spec.holes, pipes };
  compiledCache.set(spec, compiled);
  return compiled;
}

function sdfShape(px: number, py: number, shape: FieldShape, compiled: CompiledField): number {
  if (shape.kind === 'room') return sdfRoom(px, py, shape);
  if (shape.kind === 'disc') return sdfDisc(px, py, shape);
  if (shape.kind === 'ellipse') return sdfEllipse(px, py, shape);
  if (shape.kind === 'poly') return sdfPoly(px, py, shape);
  return sdfPipe(px, py, compiled.pipes.get(shape)!);
}

/** 걷는 면 SDF — 안쪽(주행 가능)이 음수. */
export function fieldDistance(spec: FieldSpec, px: number, py: number): number {
  const compiled = compileField(spec);
  let distance = Number.POSITIVE_INFINITY;
  for (const shape of compiled.shapes) distance = Math.min(distance, sdfShape(px, py, shape, compiled));
  for (const hole of compiled.holes) distance = Math.max(distance, -sdfShape(px, py, hole, compiled));
  return distance;
}

// ---------------------------------------------------------------------------
// 마칭 스퀘어 — SDF 0 등고선을 폴리라인 루프로 추출해 물리 벽을 만든다.
// ---------------------------------------------------------------------------

function simplifyLoop(points: readonly FieldPoint[], epsilon: number): FieldPoint[] {
  if (points.length <= 4) return [...points];
  const keep = new Array<boolean>(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;
  // 닫힌 루프(첫점≈끝점)는 기준선이 퇴화하므로 첫점에서 가장 먼 점을 먼저 고정한다.
  let anchor = -1;
  let anchorDistance = 0;
  for (let index = 1; index < points.length - 1; index += 1) {
    const distance = Math.hypot(points[index].x - points[0].x, points[index].y - points[0].y);
    if (distance > anchorDistance) {
      anchorDistance = distance;
      anchor = index;
    }
  }
  const stack: Array<[number, number]> = [];
  if (anchor > 0) {
    keep[anchor] = true;
    stack.push([0, anchor], [anchor, points.length - 1]);
  } else {
    stack.push([0, points.length - 1]);
  }
  while (stack.length > 0) {
    const [from, to] = stack.pop()!;
    const a = points[from];
    const b = points[to];
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const length = Math.max(1e-6, Math.hypot(abx, aby));
    let farthest = -1;
    let farthestDistance = epsilon;
    for (let index = from + 1; index < to; index += 1) {
      const point = points[index];
      const distance = Math.abs((point.x - a.x) * aby - (point.y - a.y) * abx) / length;
      if (distance > farthestDistance) {
        farthestDistance = distance;
        farthest = index;
      }
    }
    if (farthest >= 0) {
      keep[farthest] = true;
      stack.push([from, farthest], [farthest, to]);
    }
  }
  return points.filter((_, index) => keep[index]);
}

const contourCache = new WeakMap<FieldSpec, readonly (readonly FieldPoint[])[]>();

export function fieldContours(spec: FieldSpec, worldWidth: number, worldHeight: number, cell = 8): readonly (readonly FieldPoint[])[] {
  const cached = contourCache.get(spec);
  if (cached) return cached;
  const columns = Math.ceil(worldWidth / cell) + 3;
  const rows = Math.ceil(worldHeight / cell) + 3;
  const originX = -cell;
  const originY = -cell;
  const values = new Float32Array(columns * rows);
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      values[row * columns + column] = fieldDistance(spec, originX + column * cell, originY + row * cell);
    }
  }
  const value = (column: number, row: number) => values[row * columns + column];
  const key = (x: number, y: number) => `${Math.round(x * 32)}:${Math.round(y * 32)}`;
  const segments: Array<{ a: FieldPoint; b: FieldPoint }> = [];
  const interpolate = (
    x0: number, y0: number, d0: number,
    x1: number, y1: number, d1: number,
  ): FieldPoint => {
    const t = Math.abs(d0 - d1) < 1e-9 ? .5 : d0 / (d0 - d1);
    return { x: x0 + (x1 - x0) * t, y: y0 + (y1 - y0) * t };
  };
  for (let row = 0; row < rows - 1; row += 1) {
    for (let column = 0; column < columns - 1; column += 1) {
      const x0 = originX + column * cell;
      const y0 = originY + row * cell;
      const x1 = x0 + cell;
      const y1 = y0 + cell;
      const dTL = value(column, row);
      const dTR = value(column + 1, row);
      const dBR = value(column + 1, row + 1);
      const dBL = value(column, row + 1);
      let caseIndex = 0;
      if (dTL < 0) caseIndex |= 1;
      if (dTR < 0) caseIndex |= 2;
      if (dBR < 0) caseIndex |= 4;
      if (dBL < 0) caseIndex |= 8;
      if (caseIndex === 0 || caseIndex === 15) continue;
      const top = () => interpolate(x0, y0, dTL, x1, y0, dTR);
      const right = () => interpolate(x1, y0, dTR, x1, y1, dBR);
      const bottom = () => interpolate(x0, y1, dBL, x1, y1, dBR);
      const left = () => interpolate(x0, y0, dTL, x0, y1, dBL);
      const emit = (a: FieldPoint, b: FieldPoint) => segments.push({ a, b });
      switch (caseIndex) {
        case 1: emit(left(), top()); break;
        case 2: emit(top(), right()); break;
        case 3: emit(left(), right()); break;
        case 4: emit(right(), bottom()); break;
        case 6: emit(top(), bottom()); break;
        case 7: emit(left(), bottom()); break;
        case 8: emit(bottom(), left()); break;
        case 9: emit(bottom(), top()); break;
        case 11: emit(bottom(), right()); break;
        case 12: emit(right(), left()); break;
        case 13: emit(right(), top()); break;
        case 14: emit(top(), left()); break;
        case 5: {
          const center = fieldDistance(spec, x0 + cell / 2, y0 + cell / 2);
          if (center < 0) { emit(left(), bottom()); emit(right(), top()); }
          else { emit(left(), top()); emit(right(), bottom()); }
          break;
        }
        case 10: {
          const center = fieldDistance(spec, x0 + cell / 2, y0 + cell / 2);
          if (center < 0) { emit(top(), left()); emit(bottom(), right()); }
          else { emit(top(), right()); emit(bottom(), left()); }
          break;
        }
        default: break;
      }
    }
  }
  // 세그먼트 방향이 케이스마다 다르므로 양 끝점 모두로 색인해 방향 무관하게 잇는다.
  const byEndpoint = new Map<string, Array<{ a: FieldPoint; b: FieldPoint; used: boolean }>>();
  const tagged = segments.map((segment) => ({ ...segment, used: false }));
  for (const segment of tagged) {
    for (const point of [segment.a, segment.b]) {
      const pointKey = key(point.x, point.y);
      const list = byEndpoint.get(pointKey) ?? [];
      list.push(segment);
      byEndpoint.set(pointKey, list);
    }
  }
  const loops: FieldPoint[][] = [];
  for (const seed of tagged) {
    if (seed.used) continue;
    seed.used = true;
    const loop: FieldPoint[] = [seed.a, seed.b];
    let guard = 0;
    while (guard < segments.length + 4) {
      guard += 1;
      const tail = loop[loop.length - 1];
      const tailKey = key(tail.x, tail.y);
      const next = byEndpoint.get(tailKey)?.find((candidate) => !candidate.used);
      if (!next) break;
      next.used = true;
      const forward = key(next.a.x, next.a.y) === tailKey ? next.b : next.a;
      loop.push(forward);
      if (key(forward.x, forward.y) === key(loop[0].x, loop[0].y)) break;
    }
    if (loop.length >= 4) loops.push(simplifyLoop(loop, 1.1));
  }
  contourCache.set(spec, loops);
  return loops;
}

// ---------------------------------------------------------------------------
// 리플레이 채널 — 움직이는 장애물마다 스칼라 1개(리프트는 게이트 포함 2개).
// ---------------------------------------------------------------------------

export function fieldGateIsClosed(openness: number): boolean {
  return openness < .5;
}

/** 실제 반원에만 회전 힘을 준다. 연결부 40px에서 서서히 인계해 경계 충격을 피한다. */
export function radialZoneWeight(zone: Extract<FieldZone, { kind: 'radial' }>, x: number, y: number): number {
  const distance = Math.hypot(x - zone.x, y - zone.y);
  if (distance < zone.rMin || distance > zone.rMax || distance < 1) return 0;
  if (!zone.halfPlane) return 1;
  const opposite = zone.halfPlane === 'upper' ? y - zone.y : zone.y - y;
  return Math.max(0, Math.min(1, 1 - opposite / 40));
}

export function fieldPinIsRaised(amount: number): boolean {
  return amount > .5;
}

export function fieldChannelIds(spec: FieldSpec): readonly string[] {
  const ids: string[] = [];
  if (spec.startGate || spec.extraStartGates?.length) ids.push('start-gate');
  let hasSinkPins = false;
  for (const obstacle of spec.obstacles) {
    if (obstacle.kind === 'pin') {
      if (obstacle.sink) hasSinkPins = true;
      continue;
    }
    if (obstacle.kind === 'bumper' || obstacle.kind === 'bar') continue;
    ids.push(obstacle.id);
    if (obstacle.kind === 'lift') ids.push(`${obstacle.id}:gate`);
  }
  if (hasSinkPins) ids.push('pin-sink');
  return ids;
}

// ---------------------------------------------------------------------------
// 리프트 — 사이클 스칼라(0..1) 하나로 플랫폼 위치·기울기·해치 상태를 유도한다.
// 승차(마우스 게이트 열림) → 상승 → 상단 배출(기울임) → 하강.
// ---------------------------------------------------------------------------

export interface LiftPose {
  readonly surfaceY: number;
  readonly angle: number;
  readonly boarding: boolean;
  readonly hatchOpen: boolean;
}

function easeInOut(amount: number): number {
  return amount * amount * (3 - 2 * amount);
}

export function liftTravelPose(
  lift: Extract<FieldObstacle, { kind: 'lift' }>,
  cycle: number,
): LiftPose {
  const wrapped = ((cycle % 1) + 1) % 1;
  const board = lift.boardFraction;
  const rise = lift.riseFraction;
  const dump = lift.dumpFraction;
  const travel = lift.crestSurfaceY - lift.boardSurfaceY;
  if (wrapped < board) {
    return { surfaceY: lift.boardSurfaceY, angle: 0, boarding: true, hatchOpen: false };
  }
  if (wrapped < board + rise) {
    const local = (wrapped - board) / rise;
    return {
      surfaceY: lift.boardSurfaceY + travel * easeInOut(local),
      angle: 0,
      boarding: false,
      hatchOpen: local > .62,
    };
  }
  if (wrapped < board + rise + dump) {
    const local = (wrapped - board - rise) / dump;
    const tiltEnvelope = local < .35 ? local / .35 : local > .8 ? (1 - local) / .2 : 1;
    return {
      surfaceY: lift.crestSurfaceY,
      angle: lift.dumpAngle * Math.max(0, Math.min(1, tiltEnvelope)),
      boarding: false,
      hatchOpen: true,
    };
  }
  const local = (wrapped - board - rise - dump) / Math.max(.0001, 1 - board - rise - dump);
  return {
    surfaceY: lift.crestSurfaceY - travel * easeInOut(Math.min(1, local * 1.15)),
    angle: 0,
    boarding: false,
    hatchOpen: local < .12,
  };
}

export function pointInFieldRect(x: number, y: number, rect: FieldRect): boolean {
  return x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h;
}

/** 폴리라인 스파인 ± 폭/2 캡슐 포함 판정 (대각 벨트용). */
export function pointNearPolyline(points: readonly FieldPoint[], width: number, x: number, y: number): boolean {
  const halfWidth = width / 2;
  for (let index = 0; index < points.length - 1; index += 1) {
    const a = points[index];
    const b = points[index + 1];
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const lengthSquared = Math.max(1e-6, abx * abx + aby * aby);
    const t = Math.max(0, Math.min(1, ((x - a.x) * abx + (y - a.y) * aby) / lengthSquared));
    if (Math.hypot(x - (a.x + abx * t), y - (a.y + aby * t)) <= halfWidth) return true;
  }
  return false;
}

/** 역류 펌프 분사 방향 — period 반 주기마다 좌우(±)를 바꾼다. */
export function pumpDirection(zone: Extract<FieldZone, { kind: 'pump' }>, time: number): number {
  const period = Math.max(.05, zone.period);
  const local = ((time + (zone.phase ?? 0)) % period + period) % period;
  return local < period / 2 ? 1 : -1;
}

export function inWaitZone(spec: FieldSpec, x: number, y: number): boolean {
  for (const zone of spec.zones) {
    if (zone.kind !== 'wait' || zone.waitForRelease === false) continue;
    if (zone.rect && pointInFieldRect(x, y, zone.rect)) return true;
    if (zone.circle && Math.hypot(x - zone.circle.x, y - zone.circle.y) <= zone.circle.r) return true;
  }
  return false;
}

/** 대기 존의 추가 감쇠 계수 — 고속 진입 마블을 붙잡아 혼전으로 만든다. */
export function waitZoneCalm(spec: FieldSpec, x: number, y: number, eventType?: RaceEventType, envelope = 0): number {
  let calm = 0;
  for (const zone of spec.zones) {
    if (zone.kind !== 'wait' || !zone.calm) continue;
    const effective = zone.eventType && zone.eventType === eventType
      ? zone.calm + ((zone.eventCalm ?? zone.calm) - zone.calm) * envelope
      : zone.calm;
    if (zone.rect && pointInFieldRect(x, y, zone.rect)) calm = Math.max(calm, effective);
    if (zone.circle && Math.hypot(x - zone.circle.x, y - zone.circle.y) <= zone.circle.r) calm = Math.max(calm, effective);
  }
  return calm;
}

export function fieldFinishContains(finish: FieldFinish, x: number, y: number): boolean {
  if (finish.kind === 'circle') return Math.hypot(x - finish.x, y - finish.y) <= finish.r;
  return pointInFieldRect(x, y, { x: finish.x, y: finish.y, w: finish.w, h: finish.h });
}
